import cx from 'classnames';
import React, { Component, KeyboardEvent } from 'react';

type HeaderProps = {
  activeIndex: number;
  tabs: Array<any>;
  isFinished?: boolean;
  persistOnTabChange?: boolean;
  onSelect: (tab: number) => void;
  data: Record<string, any>;
  idPrefix: string;
};

export const tabId = (idPrefix: string, index: number) => `${idPrefix}-tab-${index}`;
export const tabPanelId = (idPrefix: string, index: number) => `${idPrefix}-panel-${index}`;

class Header extends Component<HeaderProps> {
  tabRefs: Array<HTMLDivElement | null> = [];

  isDisabled = (index: number) => {
    const { tabs, activeIndex, persistOnTabChange } = this.props;

    if (persistOnTabChange && activeIndex < index) {
      return true;
    }

    for (let i = 0; i < index; i++) {
      if (!tabs[i].isFinished) {
        return true;
      }
    }
    return false;
  };

  isTabDisabled = (index: number) => !this.props.tabs[index].alwaysOpen && this.isDisabled(index);

  // Arrows move the focus between the tabs that can be opened, Enter or Space opens the focused one
  focusTab = (from: number, step: number) => {
    const { tabs } = this.props;
    for (let i = 1; i <= tabs.length; i++) {
      const index = (from + step * i + tabs.length) % tabs.length;
      if (!this.isTabDisabled(index)) {
        this.tabRefs[index]?.focus();
        return;
      }
    }
  };

  handleKeyDown = (e: KeyboardEvent<HTMLDivElement>, index: number) => {
    const { tabs, onSelect } = this.props;
    switch (e.key) {
      case 'ArrowRight':
        this.focusTab(index, 1);
        break;
      case 'ArrowLeft':
        this.focusTab(index, -1);
        break;
      case 'Home':
        this.focusTab(-1, 1);
        break;
      case 'End':
        this.focusTab(tabs.length, -1);
        break;
      case 'Enter':
      case ' ':
        if (!this.isTabDisabled(index)) onSelect(index);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  render() {
    const { tabs, activeIndex, onSelect, idPrefix } = this.props;

    return (
      <div className="af-tabs__header" role="tablist">
        {tabs.map((tab, index) => {
          const disabled = this.isTabDisabled(index);
          const active = index === activeIndex;
          return (
            <div
              key={index}
              ref={(el) => (this.tabRefs[index] = el)}
              id={tabId(idPrefix, index)}
              role="tab"
              aria-selected={active}
              aria-controls={tabPanelId(idPrefix, index)}
              aria-disabled={disabled || undefined}
              tabIndex={active ? 0 : -1}
              className={cx('af-tab', active && 'af-tab--active', disabled && 'af-tab--disabled')}
              onClick={() => !disabled && onSelect(index)}
              onKeyDown={(e) => this.handleKeyDown(e, index)}
            >
              {tab.label}
            </div>
          );
        })}
      </div>
    );
  }
}

export { Header };
